import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Target, Filter, MonitorSmartphone, ShieldCheck, CheckCircle2, Loader2, X, Check,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import RotatingWord from "@/components/RotatingWord";
import CaseCard from "@/components/CaseCard";
import GdprConsent from "@/components/GdprConsent";
import Seo from "@/components/Seo";
import BookingCalendar from "@/components/BookingCalendar";
import { trackLead } from "@/lib/pixel";
import { getUtmPayload } from "@/lib/utm";
import { listCaseStudies, createContact } from "@/lib/api";

const STATS = [
  { n: "+300", l: "Figure reclutate" },
  { n: "+500", l: "Aziende aiutate" },
  { n: "< 30 g", l: "Time to hire medio" },
  { n: "+30", l: "Persone nel team" },
];

const BLOCKS = [
  {
    icon: Target,
    title: "10 milioni di figure professionali verificate",
    body:
      "Non aspettiamo candidature. Identifichiamo i profili più coerenti con la tua ricerca e li contattiamo direttamente, con messaggi personalizzati, gestiti da un recruiter Lovac. Spesso sono persone che non stai cercando ancora, perché non le trovi sul mercato visibile.",
    image: "/images/metodo-box2-identificazione.png",
  },
  {
    icon: Filter,
    title: "La selezione la facciamo noi.",
    body:
      "Ogni candidato che ti presentiamo è già stato intervistato da un professionista Lovac. L'AI analizza la coerenza con il brief. I recruiter valutano motivazione, contesto, compatibilità. Tu incontri solo chi vale il tuo tempo.",
    image: "/images/metodo-box1-apertura.png",
  },
  {
    icon: MonitorSmartphone,
    title: "Sai sempre cosa sta succedendo.",
    body:
      "Dashboard proprietaria con stato della ricerca, schede candidato AI, pipeline aggiornata in tempo reale. Niente \"ti richiamo la settimana prossima\". Contatto diretto con il tuo recruiter dedicato.",
    image: "/images/metodo-hero-1.png",
  },
];

const COMPARE = [
  ["Annunci + CV in entrata", "Hunting attivo su 10M profili"],
  ["Chi è disponibile", "Chi vale la pena cercare"],
  ["Lo screening lo fai tu", "Lo fa il recruiter Lovac"],
  ["Nessuna visibilità", "Dashboard in tempo reale"],
  ["Paghi comunque", "Rimborso se non troviamo chi cerchi"],
];

const FAQ = [
  {
    q: "Abbiamo già usato LinkedIn da soli.",
    a: "LinkedIn è il canale. Il problema è il metodo. Lovac accede a un database di 10 milioni di figure professionali verificate e le contatta con outreach mirato su profili già selezionati. Poi c'è lo screening, i colloqui, la valutazione. Non è compatibile con il tuo lavoro quotidiano.",
  },
  {
    q: "Abbiamo già provato un'agenzia. Non ha funzionato.",
    a: "Lo sentiamo spesso. Il problema delle agenzie tradizionali è strutturale: aspettano candidature in entrata e mandano chi è disponibile. Lovac fa il contrario. E se non porta candidati coerenti con quello che cerchi, rimborsa l'acconto.",
  },
  {
    q: "L'AI seleziona le persone?",
    a: "No. L'AI analizza profili e produce score. Ma ogni candidato che ti presentiamo è stato valutato da un recruiter Lovac, colloquio incluso. L'AI ci permette di farlo più velocemente e meglio. La decisione è sempre umana.",
  },
  {
    q: "Quanto costa?",
    a: "Il modello è a success fee, paghi quando hai il risultato. Il costo reale non è la fee: è il costo di una posizione aperta per tre mesi. Produttività persa, team sotto pressione, opportunità mancate. Parlacene in call, ti diciamo come siamo strutturati per la tua ricerca specifica.",
  },
];

const EMPTY = {
  motivo_richiesta: "",
  nome: "",
  cognome: "",
  azienda: "",
  dimensione_azienda: "",
  ruolo: "",
  ruolo_cercato: "",
  telefono: "",
  email: "",
};

/* ------------- Stat (light surface) ------------- */
const Stat = ({ n, l }) => (
  <div className="text-center sm:text-left">
    <p className="font-display text-4xl sm:text-5xl text-on-dark leading-none">{n}</p>
    <p className="text-xs sm:text-sm text-on-dark-muted mt-2 uppercase tracking-wider">{l}</p>
  </div>
);

/* ------------- Section header ------------- */
const SectionHeader = ({ kicker, title, children }) => (
  <div className="max-w-3xl">
    {kicker && <span className="badge-lovac">{kicker}</span>}
    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-lovac-dark">
      {title}
    </h2>
    {children && <div className="mt-6 text-lg text-lovac-muted leading-relaxed">{children}</div>}
  </div>
);

export default function LandingAds() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await listCaseStudies();
        setCases((data || []).slice(0, 3));
      } catch (e) {
        setCases([]);
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = ["motivo_richiesta", "nome", "cognome", "azienda", "dimensione_azienda", "ruolo", "ruolo_cercato", "telefono", "email"];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Compila tutti i campi obbligatori");
        return;
      }
    }
    if (!gdpr) {
      toast.error("Devi accettare la Privacy Policy per procedere");
      return;
    }
    // Off-target = cerca lavoro / disoccupato → lead va comunque a GHL ma
    // NON scattiamo l'evento Meta Lead e mandiamo alla thank-you fuori target
    const isOffTarget =
      form.motivo_richiesta === "Sto cercando un lavoro" ||
      form.ruolo === "disoccupato";
    setLoading(true);
    try {
      await createContact({
        ...form,
        sorgente: isOffTarget ? "lp-prenota-call-off-target" : "lp-prenota-call",
        ...getUtmPayload(),
      });
      if (isOffTarget) {
        navigate("/lp/prenota-call/off-target", { replace: true });
        return;
      }
      trackLead({ content_name: "LP prenota-call", content_category: "lp-prenota-call" });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  /* -------- DONE STATE, booking step -------- */
  if (done) {
    return (
      <>
        {/* MINI-HEADER, solo logo, nessun menu */}
        <div className="bg-lovac border-b border-lovac">
          <div className="container-lovac px-6 py-5 flex items-center">
            <img src="/images/logo-orizzontale.png" alt="Lovac" className="h-7 sm:h-8" />
          </div>
        </div>

        <section
          className="py-20 lg:py-24 bg-lovac-dark text-on-dark grain relative overflow-hidden"
          data-testid="lp-done-section"
        >
          <div className="container-narrow relative z-10">
            <div className="text-center">
              <CheckCircle2 size={56} className="text-lovac-accent mx-auto" />
              <h1
                className="font-display text-3xl sm:text-4xl lg:text-5xl mt-6 text-on-dark leading-tight"
                data-testid="lp-done-title"
              >
                Un ultimo step per completare la tua richiesta.
              </h1>
              <p className="text-on-dark-muted mt-5 max-w-xl mx-auto leading-relaxed">
                La richiesta è già arrivata al nostro team. Scegli qui sotto lo slot che preferisci
                per parlare con un consulente Lovac.
              </p>
            </div>

            <div
              className="mt-10 rounded-2xl bg-white overflow-hidden shadow-2xl"
              data-testid="lp-booking-wrapper"
            >
              <BookingCalendar
                id="booking-lp-prenota-call"
                prefill={{
                  first_name: form.nome,
                  last_name: form.cognome,
                  email: form.email,
                  phone: form.telefono,
                  company: form.azienda,
                }}
              />
            </div>

            <div className="flex flex-wrap justify-center gap-3 mt-10">
              <Link to="/casi-studio" className="btn-outline-dark" target="_blank">
                Guarda i casi studio <ArrowRight size={16} />
              </Link>
              <Link to="/metodo" className="btn-outline-dark" target="_blank">
                Scopri il metodo <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo title="Prenota una call con Lovac" description="Trovi figure senior in meno di 30 giorni. Prenota una call con un recruiter Lovac." path="/lp/prenota-call" noindex />
      {/* MINI-HEADER, solo logo, nessun menu */}
      <div className="bg-lovac border-b border-lovac">
        <div className="container-lovac px-6 py-5 flex items-center">
          <img src="/images/logo-orizzontale.png" alt="Lovac" className="h-7 sm:h-8" />
        </div>
      </div>

      {/* HERO */}
      <section className="bg-lovac relative overflow-hidden" data-testid="lp-hero">
        <div className="container-lovac px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <span className="badge-lovac">Recruiting tech-driven per PMI italiane</span>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl mt-7 leading-[1.02] text-lovac-dark max-w-5xl">
            <span>2 aziende su 3</span>{" "}
            <span className="text-lovac-accent">non trovano</span>{" "}
            <span>il personale che cercano.</span>
          </h1>

          <p className="text-2xl sm:text-3xl lg:text-4xl text-lovac-dark mt-10 max-w-3xl leading-snug font-semibold">
            Lovac ti aiuta a trovare i <RotatingWord />
            <br />
            che cerchi in meno di 30 giorni.
          </p>

          <p className="text-base sm:text-lg text-lovac-muted mt-7 max-w-2xl leading-relaxed">
            Non aspettiamo i CV. Andiamo a cercare chi vale tra <strong className="text-lovac-dark font-semibold">oltre 10 milioni di profili verificati</strong>,
            intercettando anche chi non sta cercando lavoro in questo momento. Paghi solo se funziona.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#form" className="btn-primary text-base px-7 py-4">
              Richiedi informazioni <ArrowRight size={18} />
            </a>
            <p className="text-sm text-lovac-muted max-w-sm">
              Nessun impegno. Se non siamo la soluzione giusta, te lo diciamo.
            </p>
          </div>

          {/* Fonte in angolino */}
          <p className="absolute bottom-5 right-6 text-[11px] text-lovac-muted italic">
            Fonte: Confindustria, 2025
          </p>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 py-14">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 sm:gap-8 items-center">
            {STATS.map((s) => (
              <Stat key={s.l} n={s.n} l={s.l} />
            ))}
          </div>
        </div>
      </section>

      {/* IL PROBLEMA */}
      <section className="section bg-lovac">
        <div className="container-lovac">
          <SectionHeader title={
            <>
              Il problema non è che non esistono.<br />
              <span className="text-lovac-muted">È che quelli bravi non mandano CV.</span>
            </>
          } />
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mt-12">
            <div className="text-base sm:text-lg text-lovac-dark leading-[1.75] space-y-5">
              <p>Un manager con un track record solido non è su Indeed.</p>
              <p>Un tecnico specializzato con esperienza reale non risponde agli annunci.</p>
              <p>Una figura senior che produce risultati è già al lavoro, e non ha nessun motivo urgente per muoversi.</p>
              <p className="text-lovac-dark font-display text-2xl">Se aspetti che arrivino da soli, aspetti a lungo.</p>
            </div>
            <div className="text-base sm:text-lg text-lovac-muted leading-[1.75] space-y-5 lg:border-l lg:border-lovac lg:pl-12">
              <p>
                Le agenzie tradizionali hanno lo stesso problema: pubblicano annunci sugli stessi portali che useresti tu,
                raccolgono chi è disponibile, ti mandano una lista.
              </p>
              <p>Il lavoro vero, scremare, valutare, decidere, lo fai comunque tu.</p>
              <p className="text-lovac-dark font-medium">
                Hai pagato per un servizio che avresti potuto fare da solo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LA SOLUZIONE */}
      <section className="section bg-lovac-2">
        <div className="container-lovac">
          <SectionHeader
            kicker="La soluzione"
            title="Lovac va a cercare chi non stava cercando."
          />

          <div className="mt-20 space-y-16 lg:space-y-20">
            {BLOCKS.map((b, i) => {
              const Icon = b.icon;
              const reverse = i % 2 === 1;
              return (
                <div
                  key={b.title}
                  className={`grid lg:grid-cols-2 gap-10 lg:gap-14 items-center ${
                    reverse ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div>
                    <div
                      className="h-12 w-12 rounded-xl flex items-center justify-center mb-6"
                      style={{
                        background: "rgba(0,196,140,0.14)",
                        border: "0.5px solid rgba(0,196,140,0.25)",
                      }}
                    >
                      <Icon size={20} className="text-lovac-accent" />
                    </div>
                    <p className="font-mono text-xs uppercase tracking-wider text-lovac-muted">
                      0{i + 1} / 03
                    </p>
                    <h3 className="font-display text-3xl sm:text-4xl text-lovac-dark mt-3 leading-tight">
                      {b.title}
                    </h3>
                    <p className="text-base text-lovac-dark mt-6 leading-relaxed">{b.body}</p>
                  </div>
                  <div className="flex items-center justify-center">
                    <img
                      src={b.image}
                      alt={b.title}
                      className="w-full h-auto max-w-[560px] mx-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <a href="#form" className="btn-primary text-base px-7 py-4">
              Richiedi informazioni <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>
      <section className="section bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac relative z-10">
          <div className="max-w-5xl mx-auto">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center mb-8"
              style={{
                background: "rgba(0,196,140,0.14)",
                border: "0.5px solid rgba(0,196,140,0.25)",
              }}
            >
              <ShieldCheck size={20} className="text-lovac-accent" />
            </div>

            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start">
              <div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-on-dark">
                  Paghi solo se funziona.<br />
                  <span className="text-lovac-accent">Se non funziona, ti restituiamo l'acconto.</span>
                </h2>
              </div>
              <div className="text-base sm:text-lg text-on-dark-muted leading-relaxed space-y-5 lg:pt-3">
                <p>Il modello Lovac è a success fee: paghi a inserimento avvenuto.</p>
                <p>L'acconto iniziale è garantito e rimborsabile, se non presentiamo candidati coerenti con il tuo brief, lo restituiamo. Intero.</p>
                <p className="text-on-dark font-medium">Non è una clausola in piccolo. È il modo in cui scegliamo di lavorare.</p>
              </div>
            </div>

            {/* Tabella comparativa */}
            <div className="mt-20">
              <div className="grid grid-cols-2 gap-px rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="bg-lovac-dark p-5 sm:p-6">
                  <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">Agenzie tradizionali</p>
                </div>
                <div className="bg-lovac-dark p-5 sm:p-6 flex items-center gap-2">
                  <p className="text-[11px] uppercase tracking-wider text-lovac-accent">Lovac</p>
                </div>
                {COMPARE.map(([a, b]) => (
                  <div className="contents" key={a}>
                    <div className="bg-lovac-dark p-5 sm:p-6 text-on-dark-muted flex items-start gap-3">
                      <X size={16} className="mt-1 text-on-dark-muted shrink-0" />
                      <span className="text-sm sm:text-base leading-relaxed">{a}</span>
                    </div>
                    <div className="bg-lovac-dark p-5 sm:p-6 text-on-dark flex items-start gap-3">
                      <Check size={16} className="mt-1 text-lovac-accent shrink-0" />
                      <span className="text-sm sm:text-base leading-relaxed">{b}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-14 text-center">
              <a href="#form" className="btn-primary text-base px-7 py-4">
                Richiedi informazioni <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>
      {cases.length > 0 && (
        <section className="section bg-lovac">
          <div className="container-lovac">
            <SectionHeader
              kicker="Casi studio"
              title="Chi ha smesso di aspettare e si è affidato a Lovac."
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
              {cases.map((c) => <CaseCard key={c.slug} c={c} />)}
            </div>
            <div className="mt-10">
              <Link to="/casi-studio" target="_blank" className="link-underline text-lovac-dark inline-flex items-center gap-2">
                Guarda tutti i casi studio <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FORM */}
      <section id="form" className="section bg-lovac-2" data-testid="lp-form">
        <div className="container-lovac">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20">
            <div>
              <span className="badge-lovac">Prenota una call</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-lovac-dark">
                Raccontaci cosa stai cercando.
              </h2>
              <p className="text-lg text-lovac-muted mt-6 leading-relaxed max-w-md">
                Ti diciamo subito se possiamo aiutarti, e come.
              </p>
              <div className="mt-10 space-y-4">
                {[
                  "Nessun impegno",
                  "Confronto diretto con un recruiter Lovac",
                  "Se non siamo la soluzione, te lo diciamo",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-lovac-accent mt-1 shrink-0" />
                    <p className="text-base text-lovac-dark">{t}</p>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={onSubmit} className="card-lovac space-y-4" data-testid="lp-form-card" data-nocapture="true" data-ghl-ignore="true" data-lc-ignore="true">
              <Select
                label="Motivo della richiesta *"
                value={form.motivo_richiesta}
                onChange={(v) => setForm({ ...form, motivo_richiesta: v })}
                options={[
                  { value: "Sto cercando personale da assumere", label: "Sto cercando personale da assumere" },
                  { value: "Sto cercando un lavoro", label: "Sto cercando un lavoro" },
                ]}
                testId="lp-input-motivo"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="Nome *"
                  value={form.nome}
                  onChange={(v) => setForm({ ...form, nome: v })}
                  testId="lp-input-nome"
                />
                <Input
                  label="Cognome *"
                  value={form.cognome}
                  onChange={(v) => setForm({ ...form, cognome: v })}
                  testId="lp-input-cognome"
                />
              </div>
              <Input
                label="Azienda *"
                value={form.azienda}
                onChange={(v) => setForm({ ...form, azienda: v })}
                testId="lp-input-azienda"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Select
                  label="Numero di dipendenti *"
                  value={form.dimensione_azienda}
                  onChange={(v) => setForm({ ...form, dimensione_azienda: v })}
                  options={["<10", "10-25", "26-50", "50-100", "100+"]}
                  testId="lp-input-dimensione"
                />
                <Select
                  label="Il tuo ruolo *"
                  value={form.ruolo}
                  onChange={(v) => setForm({ ...form, ruolo: v })}
                  options={[
                    { value: "ceo__founder", label: "CEO / Founder" },
                    { value: "direttore_hr", label: "Direttore HR" },
                    { value: "responsabile_di_funzione", label: "Responsabile di funzione" },
                    { value: "coo", label: "COO" },
                    { value: "operation_manager", label: "Operation manager" },
                    { value: "altro", label: "Altro" },
                    { value: "disoccupato", label: "Disoccupato" },
                  ]}
                  testId="lp-input-ruolo"
                />
              </div>
              <Input
                label="Ruolo cercato *"
                value={form.ruolo_cercato}
                onChange={(v) => setForm({ ...form, ruolo_cercato: v })}
                testId="lp-input-ruolo-cercato"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="Telefono *"
                  value={form.telefono}
                  onChange={(v) => setForm({ ...form, telefono: v })}
                  testId="lp-input-telefono"
                />
                <Input
                  label="Email *"
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  testId="lp-input-email"
                />
              </div>

              <div className="pt-3 mt-1 border-t border-lovac">
                <GdprConsent checked={gdpr} onChange={setGdpr} variant="light" testId="lp-gdpr" />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center mt-3 disabled:opacity-60"
                data-testid="lp-form-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Invio in corso…
                  </>
                ) : (
                  <>
                    Invia la richiesta <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-xs text-lovac-muted mt-3 text-center leading-relaxed">
                Nessun impegno. Se non siamo la soluzione giusta per la tua ricerca, te lo diciamo subito.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-lovac">
        <div className="container-lovac">
          <SectionHeader title="Domande che ci fanno spesso." />
          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {FAQ.map((f, i) => (
              <Accordion type="single" collapsible key={f.q} className="card-lovac p-0 overflow-hidden">
                <AccordionItem value={`q-${i}`} className="border-0">
                  <AccordionTrigger
                    className="font-display text-xl sm:text-2xl text-lovac-dark hover:no-underline px-7 py-6 text-left"
                    data-testid={`lp-faq-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-lovac-muted leading-relaxed px-7 pb-7">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="section bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-narrow text-center relative z-10">
          <span className="badge-on-dark">Prenota una call</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 text-on-dark leading-tight">
            Hai una posizione aperta<br />da troppo tempo?
          </h2>
          <p className="text-on-dark-muted mt-5 text-base max-w-xl mx-auto">
            Parlacene. 30 minuti, nessun impegno.
          </p>
          <div className="flex justify-center mt-10">
            <a href="#form" className="btn-primary">
              Prenota una call gratuita <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

/* ---------- Form fields ---------- */
function Input({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-lovac-muted">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full mt-2 px-4 py-3 rounded-lg border border-lovac bg-white text-lovac-dark text-base focus:outline-none focus:border-lovac-accent transition-colors"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  // options may be an array of strings (label = value) OR an array of
  // { value, label } for cases where the CRM (GHL) needs a specific slug
  // that differs from the human-readable label.
  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-lovac-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full mt-2 px-4 py-3 rounded-lg border border-lovac bg-white text-lovac-dark text-base focus:outline-none focus:border-lovac-accent transition-colors"
      >
        <option value="">Seleziona…</option>
        {normalized.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
